import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Share2, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const StoryCardGenerator = ({ storyData }) => {
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  const downloadCard = async () => {
    setDownloading(true);
    try {
      const element = cardRef.current;
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        scale: 2,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `${storyData.destination.city}-safety-report.jpg`;
      link.href = canvas.toDataURL('image/jpeg',0.92);
      link.click();
      
      toast.success('Story card downloaded!');
    } catch (error) {
      toast.error('Failed to download card');
    } finally {
      setDownloading(false);
    }
  };

  const shareToWhatsApp = () => {
    const text = `${storyData.sharing.title}\n${storyData.sharing.description}\n\n${storyData.sharing.url}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareToInstagram = () => {
    // Instagram doesn't support direct sharing, so we download and show instructions
    downloadCard();
    toast.info('Card downloaded! Open Instagram and upload it to your story.');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(storyData.sharing.url);
    toast.success('Link copied to clipboard!');
  };

  return (
    <div className="space-y-4">
      {/* Story Card Preview */}
      <div 
        ref={cardRef} 
        className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl"
        style={{ aspectRatio: '9/16' }}
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${storyData.destination.image_url})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
        </div>

        {/* Content */}
        <div className="relative h-full flex flex-col justify-between text-white" style={{ padding: '40px 30px' }}>
          {/* Header */}
          <div style={{ marginBottom: '20px' }}>
            <div className="flex items-center gap-2">
              <div 
                className="rounded-full bg-gradient-to-br from-purple-500 to-teal-500 flex items-center justify-center"
                style={{ width: '48px', height: '48px' }}
              >
                <span style={{ fontSize: '24px' }}>🛡️</span>
              </div>
              <div>
                <div className="font-bold" style={{ fontSize: '18px', lineHeight: '1.3', marginBottom: '4px' }}>
                  SafeShe Atlas
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9, lineHeight: '1.2' }}>
                  Women's Safety Report
                </div>
              </div>
            </div>
          </div>

          {/* Center - City Info */}
          <div className="text-center" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h2 
              className="font-bold drop-shadow-lg" 
              style={{ fontSize: '48px', lineHeight: '1.2', marginBottom: '12px' }}
            >
              {storyData.destination.city}
            </h2>
            <p style={{ fontSize: '20px', opacity: 0.9, marginBottom: '40px', lineHeight: '1.3' }}>
              {storyData.destination.country}
            </p>

            {/* WSI Score */}
            <div style={{ display: 'inline-block', margin: '0 auto' }}>
              <div 
                className="font-black"
                style={{ 
                  fontSize: '80px', 
                  lineHeight: '1',
                  marginBottom: '20px',
                  color: storyData.safety.color 
                }}
              >
                {storyData.safety.wsi}
              </div>
              <div 
                className="font-semibold" 
                style={{ fontSize: '24px', marginBottom: '24px', lineHeight: '1.2' }}
              >
                Safety Index
              </div>
              <div 
                className="rounded-full text-white font-bold"
                style={{ 
                  backgroundColor: storyData.safety.color,
                  padding: '16px 32px',
                  fontSize: '18px',
                  minWidth: '220px',
                  lineHeight: '1.4'
                }}
              >
                <div style={{ marginBottom: '8px', fontSize: '24px' }}>
                  {storyData.safety.emoji}
                </div>
                <div style={{ whiteSpace: 'nowrap' }}>
                  {storyData.safety.level}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom - Metrics & Trend */}
          <div style={{ marginTop: '20px' }}>
            <div 
              className="grid grid-cols-3" 
              style={{ gap: '20px', marginBottom: '20px' }}
            >
              <div className="text-center">
                <div className="font-bold" style={{ fontSize: '28px', marginBottom: '6px' }}>
                  {storyData.metrics.emergency}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8, lineHeight: '1.2' }}>
                  Emergency
                </div>
              </div>
              <div className="text-center">
                <div className="font-bold" style={{ fontSize: '28px', marginBottom: '6px' }}>
                  {storyData.metrics.transport}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8, lineHeight: '1.2' }}>
                  Transport
                </div>
              </div>
              <div className="text-center">
                <div className="font-bold" style={{ fontSize: '28px', marginBottom: '6px' }}>
                  {storyData.metrics.night}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8, lineHeight: '1.2' }}>
                  Night
                </div>
              </div>
            </div>

            <div className="text-center" style={{ marginBottom: '16px', fontSize: '18px', lineHeight: '1.4' }}>
              <span>
                {storyData.trend.emoji} Trend: {storyData.trend.text}
              </span>
            </div>

            <div className="text-center" style={{ fontSize: '14px', opacity: 0.8 }}>
              safesheatlas.com
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-center" data-testid="story-card-actions">
        <Button
          onClick={downloadCard}
          disabled={downloading}
          className="bg-gradient-to-r from-purple-600 to-teal-600 text-white"
          data-testid="download-story-button"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloading ? 'Downloading...' : 'Download'}
        </Button>

        <Button
          onClick={shareToWhatsApp}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="share-whatsapp-button"
        >
          <Share2 className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>

        <Button
          onClick={shareToInstagram}
          className="bg-gradient-to-r from-pink-500 to-purple-600 text-white"
          data-testid="share-instagram-button"
        >
          <Instagram className="w-4 h-4 mr-2" />
          Instagram
        </Button>

        <Button
          onClick={copyLink}
          variant="outline"
          data-testid="copy-link-button"
        >
          <Share2 className="w-4 h-4 mr-2" />
          Copy Link
        </Button>
      </div>

      <p className="text-center text-sm text-gray-600">
        Share this safety report with friends and fellow travelers!
      </p>

      {/* 🔥 Short Disclaimer */}
      <p className="text-center text-xs text-gray-500 mt-1">
        Safety scores are indicative only. Refer to our <a href="/disclaimer" className="text-teal-600 underline">Advisory</a>.
      </p>

    </div>
  );
};

export default StoryCardGenerator;